import {
    useEffect,
    useRef,
    type FocusEvent,
    type KeyboardEvent,
    type ReactNode,
    type RefObject
} from 'react';
import { useLayerEscape } from '../../../../hooks/useLayerEscape';

// In priority order: querySelector with a selector list would return the first match in the DOM.
const FOCUS_TARGETS = ['[aria-pressed="true"]', 'button:not([disabled])', 'input:not([disabled])'];
const ITEM_SELECTOR = 'button:not([disabled])';
const ALIGN_CLASS = { left: 'left-0', right: 'right-0' } as const;

export type PopoverCloseReason = 'escape' | 'outside' | 'choice';

interface BrowserPopoverProps {
    id: string;
    label: string;
    // Wraps the trigger and the popover, so a click on the trigger isn't "outside".
    anchorRef: RefObject<HTMLElement>;
    align?: keyof typeof ALIGN_CLASS;
    className?: string;
    onClose: (reason: PopoverCloseReason) => void;
    children: ReactNode;
}

function moveFocus(container: HTMLElement, step: 1 | -1) {
    const items = Array.from(container.querySelectorAll<HTMLElement>(ITEM_SELECTOR));
    const index = items.findIndex((item) => item === document.activeElement);
    if (index === -1) return;
    items[(index + step + items.length) % items.length]?.focus();
}

// Escape is taken as its own layer, so it closes this popover and not the browser modal under it.
export function BrowserPopover({
    id,
    label,
    anchorRef,
    align = 'left',
    className = '',
    onClose,
    children
}: BrowserPopoverProps) {
    const popoverRef = useRef<HTMLDivElement>(null);
    const onCloseRef = useRef(onClose);
    useLayerEscape(() => onCloseRef.current('escape'));

    useEffect(() => {
        onCloseRef.current = onClose;
    }, [onClose]);

    useEffect(() => {
        const popover = popoverRef.current;
        if (!popover || popover.contains(document.activeElement)) return;
        FOCUS_TARGETS.map((selector) => popover.querySelector<HTMLElement>(selector))
            .find((element) => element !== null)
            ?.focus();
    }, []);

    useEffect(() => {
        const handleDown = (event: MouseEvent) => {
            if (anchorRef.current?.contains(event.target as Node)) return;
            onCloseRef.current('outside');
        };
        document.addEventListener('mousedown', handleDown);
        return () => document.removeEventListener('mousedown', handleDown);
    }, [anchorRef]);

    const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
        if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;
        if (!(document.activeElement instanceof HTMLButtonElement)) return;
        event.preventDefault();
        if (popoverRef.current) moveFocus(popoverRef.current, event.key === 'ArrowDown' ? 1 : -1);
    };

    // Tabbing out closes it; a null relatedTarget is a click on the popover's own padding.
    const handleBlur = (event: FocusEvent<HTMLDivElement>) => {
        const next = event.relatedTarget;
        if (next instanceof Node && !anchorRef.current?.contains(next)) onClose('outside');
    };

    return (
        <div
            ref={popoverRef}
            id={id}
            role="dialog"
            aria-label={label}
            onKeyDown={handleKeyDown}
            onBlur={handleBlur}
            className={`absolute top-full z-30 mt-1.5 min-w-[208px] rounded-[10px] border border-gray-200 bg-white p-1 shadow-[0_10px_28px_rgba(0,0,0,0.13)] ${ALIGN_CLASS[align]} ${className}`}
        >
            {children}
        </div>
    );
}
